import type { ComponentType } from 'react';
import type { SupportedLocale } from '@cavaquinho/shared';
import type { SubmitAttemptResponse } from '../../lib/api/types.js';

/**
 * The Web half of the Exercise Type registry
 * (contracts/exercise-type-registry.md § 2).
 *
 * It is the mirror of `apps/api/src/exercises/registry.ts` and carries the
 * same claim from the other side of the boundary: adding an Exercise Type is
 * a handler there and a renderer here, and nothing else — no change to the
 * practice shell, no branch in a screen, no table, no migration (SC-009).
 *
 * The two registries share only the key. They are deliberately not one
 * artefact: the API's holds domain behaviour, this one holds presentation,
 * and `apps/web` imports nothing from `apps/api` (research D-017).
 */

/** The outcome of a submission, as the shell hands it back to the renderer. */
export type SubmitResult = SubmitAttemptResponse;

/**
 * Where the shell is in the loop. A renderer reads it rather than tracking its
 * own: the FR-023 timing gate, the "Next" action, and focus management belong
 * to the type-agnostic shell, so the phase has exactly one owner.
 */
export type ExercisePhase = 'answering' | 'submitting' | 'feedback';

/**
 * Everything a renderer receives. `publicPrompt` and `feedback` are `unknown`
 * on purpose: the shell cannot know a type's payload shape, and the renderer
 * narrows its own. That is the whole reason this registry is untyped at the
 * key and typed at the leaf.
 */
export interface ExerciseRendererProps {
  readonly promptId: string;
  /** Never enough to derive the answer — the server withholds it (research D-012). */
  readonly publicPrompt: unknown;
  readonly onSubmit: (response: unknown) => Promise<SubmitResult>;
  readonly onNext: () => void;
  readonly phase: ExercisePhase;
  /** `publicFeedback`, once the attempt is durably recorded (FR-055). */
  readonly feedback: unknown | null;
}

export type ExerciseRenderer = ComponentType<ExerciseRendererProps>;

/** An Exercise Type key this build carries no renderer for. */
export class UnknownExerciseRendererError extends Error {
  constructor(readonly exerciseType: string) {
    super(`No renderer is registered for Exercise Type "${exerciseType}"`);
    this.name = 'UnknownExerciseRendererError';
  }
}

/**
 * How a MISS IS NAMED after the run is over.
 *
 * The session summary lists what got away — "Sol, ouviu Mi" — and that is a
 * sentence only the Exercise Type can write: the shell holds `publicFeedback`
 * as `unknown` and must keep holding it that way, or the claim that a new type
 * costs a handler and a renderer stops being true (SC-009).
 *
 * So a type may register a describer alongside its renderer. Registering one
 * is OPTIONAL, and the summary is complete without it: a type that has nothing
 * short to say about a miss simply contributes no line, and the counts above
 * it are unaffected.
 */
export interface MissDescription {
  /** The short string that IS the content — the answer, in the learner's own vocabulary. */
  readonly token: string;
  /** What was given instead, as a phrase that follows the token. */
  readonly detail: string;
}

/** Returns null for a payload it does not recognise, or for a hit. */
export type MissDescriber = (feedback: unknown, locale: SupportedLocale) => MissDescription | null;

/**
 * How ONE RECORDED ANSWER IS NAMED when the learner looks back over a Lesson.
 *
 * The same problem `MissDescriber` solves, at the other end of the run, and
 * deliberately a second registration rather than a widening of the first. A
 * miss describer writes half a sentence for a list of things that got away
 * ("Sol, ouviu Mi") and says nothing at all about a hit, because a summary of
 * a sitting is not a transcript of it. A review IS a transcript: every
 * answer gets a line, right ones included, and the line has the prompt to
 * hand — so it can name what was asked, which the summary never can.
 *
 * Registering one is OPTIONAL and the review is complete without it: a type
 * that has nothing short to say contributes a row with its verdict and no
 * detail, which is still the answer to "how did I do on this one".
 */
export interface AnswerDescription {
  /** What was asked, in the learner's own vocabulary — a note, a question. */
  readonly prompt: string | null;
  /** The right answer, as a short token. */
  readonly token: string;
  /** What the learner gave, as a phrase that follows the token. */
  readonly detail: string;
}

export type AnswerDescriber = (
  publicPrompt: unknown,
  feedback: unknown,
  locale: SupportedLocale,
) => AnswerDescription | null;

const renderers = new Map<string, ExerciseRenderer>();
const missDescribers = new Map<string, MissDescriber>();
const answerDescribers = new Map<string, AnswerDescriber>();

/**
 * Called once per type at module load. A duplicate key throws rather than
 * overwriting: two renderers claiming one key is a build mistake, and the last
 * import to run should not decide which of them a learner sees.
 */
export function registerRenderer(exerciseType: string, renderer: ExerciseRenderer): void {
  if (renderers.has(exerciseType)) {
    throw new Error(`A renderer is already registered for Exercise Type "${exerciseType}"`);
  }
  renderers.set(exerciseType, renderer);
}

/**
 * Lookup for the practice shell. An unknown key throws — never a silent
 * fallback to another type, which would show a learner one Exercise while the
 * ledger recorded another. The shell turns this into a visible failure state.
 */
export function getRenderer(exerciseType: string): ExerciseRenderer {
  const renderer = renderers.get(exerciseType);
  if (!renderer) throw new UnknownExerciseRendererError(exerciseType);
  return renderer;
}

/** Registered beside the renderer, at module load, and at most once per type. */
export function registerMissDescriber(exerciseType: string, describe: MissDescriber): void {
  if (missDescribers.has(exerciseType)) {
    throw new Error(`A miss describer is already registered for Exercise Type "${exerciseType}"`);
  }
  missDescribers.set(exerciseType, describe);
}

/**
 * Names one miss, or nothing.
 *
 * Unlike `getRenderer`, an unregistered type is NOT an error here: a summary is
 * a look back at work that already happened correctly, and it must not become
 * a failure screen because a type chose to describe nothing.
 */
export function describeMiss(
  exerciseType: string,
  feedback: unknown,
  locale: SupportedLocale,
): MissDescription | null {
  return missDescribers.get(exerciseType)?.(feedback, locale) ?? null;
}

/** Registered beside the renderer, at module load, and at most once per type. */
export function registerAnswerDescriber(exerciseType: string, describe: AnswerDescriber): void {
  if (answerDescribers.has(exerciseType)) {
    throw new Error(`An answer describer is already registered for Exercise Type "${exerciseType}"`);
  }
  answerDescribers.set(exerciseType, describe);
}

/**
 * Names one recorded answer, or nothing.
 *
 * Unregistered is not an error, for the same reason it is not one above: the
 * review looks back at work that already happened, and it must not become a
 * failure screen because a type chose to describe nothing.
 */
export function describeAnswer(
  exerciseType: string,
  publicPrompt: unknown,
  feedback: unknown,
  locale: SupportedLocale,
): AnswerDescription | null {
  return answerDescribers.get(exerciseType)?.(publicPrompt, feedback, locale) ?? null;
}

